import React, { useEffect, useState } from "react";
import { Loader2, Pencil, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { fmtDate, useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";

const EMPTY = { name: "", email: "", password: "", role: "operator", user_type: "tenant_client",
                notify_email: "", duration_months: "", duration_days: "", duration_hours: "", modules: [] };

const CLIENT_MODULES = [
  { key: "sanctions", label: "nav.sanctions" }, { key: "pep", label: "nav.pep" },
  { key: "blacklist", label: "nav.blacklist" }, { key: "dd_natural", label: "nav.dd_natural" },
  { key: "dd_juridica", label: "nav.dd_juridica" }, { key: "declaracion", label: "nav.declaracion" },
  { key: "certificacion", label: "nav.certificacion" }, { key: "carta", label: "nav.carta" },
  { key: "dd_aseguradora", label: "nav.dd_aseguradora" },
  { key: "pj_aseguradora", label: "nav.pj_aseguradora" },
  { key: "pep_panama", label: "nav.pep_panama" },
  { key: "risk", label: "nav.risk" }, { key: "employees", label: "nav.employees" },
  { key: "suppliers", label: "nav.suppliers" }, { key: "ros", label: "nav.ros" },
  { key: "rte", label: "nav.rte" }, { key: "freeze", label: "nav.freeze" },
  { key: "manual", label: "nav.manual" }, { key: "history", label: "nav.history" },
];

export default function Users() {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => { toast.error(apiError(e)); setUsers([]); });
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user?.role !== "admin") {
    return <div className="ec-card p-10 text-center text-sm text-slate-500" data-testid="users-forbidden">{t("u.only_admin")}</div>;
  }

  const reset = () => { setForm(EMPTY); setEditingId(null); setShowForm(false); };

  const startEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: "", role: u.role,
              user_type: u.user_type || "tenant_client", notify_email: u.notify_email || "",
              duration_months: "", duration_days: "", duration_hours: "", modules: u.modules || [] });
    setEditingId(u.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const dur = { duration_months: Number(form.duration_months) || 0,
                    duration_days: Number(form.duration_days) || 0,
                    duration_hours: Number(form.duration_hours) || 0 };
      if (editingId) {
        const payload = { name: form.name, role: form.role, user_type: form.user_type };
        if (form.role === "client") payload.modules = form.modules;
        if (form.password) payload.password = form.password;
        if (form.notify_email) payload.notify_email = form.notify_email;
        else payload.clear_notify_email = true;
        if (form.duration_months !== "" || form.duration_days !== "" || form.duration_hours !== "") Object.assign(payload, dur);
        await api.patch(`/users/${editingId}`, payload);
        toast.success(t("u.updated"));
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password,
                                   role: form.role, user_type: form.user_type,
                                   modules: form.role === "client" ? form.modules : [],
                                   notify_email: form.notify_email || null, ...dur });
        toast.success(t("u.created"));
      }
      reset();
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, updates) => {
    try {
      await api.patch(`/users/${id}`, updates);
      toast.success(t("u.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const isTest = (u) => /^test_/i.test(u.name || "") || /^test_/i.test(u.email || "");
  const testCount = (users || []).filter((u) => u.id !== user.id && isTest(u)).length;
  const cleanup = async () => {
    if (!testCount) { toast.info(t("u.cleanup_none")); return; }
    if (!window.confirm(t("u.cleanup_confirm").replace("{n}", testCount))) return;
    try {
      const { data } = await api.delete("/users/test-cleanup");
      toast.success(t("u.cleanup_done").replace("{n}", data.deleted));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(t("u.delete_confirm").replace("{email}", u.email))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("u.deleted"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const isExpired = (u) => u.expires_at && new Date(u.expires_at) < new Date();

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("u.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("u.subtitle")} — {user?.tenant_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {user.is_super && (
            <button onClick={cleanup} data-testid="cleanup-test-users-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-bold hover:bg-red-50 transition-colors">
              <Trash2 className="w-4 h-4" /> {t("u.cleanup")}{testCount ? ` (${testCount})` : ""}
            </button>
          )}
          <button onClick={() => { if (showForm) reset(); else setShowForm(true); }} data-testid="add-user-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
            <UserPlus className="w-4 h-4" /> {t("u.add")}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="ec-card p-6 space-y-4" data-testid="create-user-form">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="ec-label">{t("u.name")}</label>
              <input className="ec-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-user-name" />
            </div>
            <div>
              <label className="ec-label">{t("u.email")}</label>
              <input className="ec-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editingId} data-testid="new-user-email" />
            </div>
            <div>
              <label className="ec-label">{editingId ? t("u.new_password") : t("auth.password")}</label>
              <input className="ec-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingId} minLength={8} data-testid="new-user-password" />
            </div>
            <div>
              <label className="ec-label">{t("u.role")}</label>
              <select className="ec-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                <option value="operator">{t("u.operator")}</option>
                <option value="admin">{t("u.admin")}</option>
                <option value="auditor">{t("u.auditor")}</option>
                <option value="client">{t("u.client")}</option>
                <option value="provider">{t("u.provider")}</option>
              </select>
            </div>
            {form.role === "client" && (
              <div className="sm:col-span-3">
                <label className="ec-label">{t("u.modules")}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1" data-testid="new-user-modules">
                  {CLIENT_MODULES.map((m) => (
                    <label key={m.key} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={form.modules.includes(m.key)}
                             onChange={(e) => setForm({ ...form, modules: e.target.checked ? [...form.modules, m.key] : form.modules.filter((x) => x !== m.key) })}
                             data-testid={`module-${m.key}`} />
                      {t(m.label)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="ec-label">{t("u.type")}</label>
              <select className="ec-input" value={form.user_type} onChange={(e) => setForm({ ...form, user_type: e.target.value })} data-testid="new-user-type">
                <option value="tenant_client">{t("u.tenant_client")}</option>
                <option value="platform_admin">{t("u.platform_admin")}</option>
              </select>
            </div>
            <div>
              <label className="ec-label">{t("u.notify_email")}</label>
              <input className="ec-input" type="email" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} placeholder={t("u.notify_hint")} data-testid="new-user-notify-email" />
            </div>
            <div className="sm:col-span-2">
              <label className="ec-label">{t("u.duration")}</label>
              <div className="flex gap-2">
                <input className="ec-input" type="number" min="0" placeholder={t("u.months")} value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} data-testid="new-user-duration-months" />
                <input className="ec-input" type="number" min="0" placeholder={t("u.days")} value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="new-user-duration-days" />
                <input className="ec-input" type="number" min="0" placeholder={t("u.hours")} value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} data-testid="new-user-duration-hours" />
              </div>
              <p className="text-[11px] text-slate-400 mt-1">{t("u.duration_hint")}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={busy} data-testid="create-user-submit" className="px-5 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editingId ? t("u.save") : t("u.create")}
            </button>
            {editingId && (
              <button type="button" onClick={reset} data-testid="cancel-edit-button" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t("u.cancel")}
              </button>
            )}
          </div>
        </form>
      )}

      <div className="ec-card overflow-hidden" data-testid="users-table-card">
        {users === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.name")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.email")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.type")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.role")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.status")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.expires")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{u.name}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {u.email}
                      {u.notify_email && <div className="text-[11px] text-slate-400" data-testid={`user-notify-${u.email}`}>{t("u.notify_email")}: {u.notify_email}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${u.user_type === "platform_admin" ? "bg-[#033A8C]/10 text-[#033A8C]" : "bg-slate-100 text-slate-600"}`} data-testid={`user-type-${u.email}`}>
                        {u.user_type === "platform_admin" ? t("u.platform_admin") : t("u.tenant_client")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        className="ec-input !w-auto !py-1 text-xs"
                        value={u.role}
                        onChange={(e) => patch(u.id, { role: e.target.value })}
                        disabled={u.id === user.id}
                        data-testid={`user-role-select-${u.email}`}
                      >
                        <option value="admin">{t("u.admin")}</option>
                        <option value="operator">{t("u.operator")}</option>
                        <option value="auditor">{t("u.auditor")}</option>
                        <option value="client" disabled>{t("u.client")}</option>
                        <option value="provider" disabled>{t("u.provider")}</option>
                      </select>
                    </td>
                    <td className="px-4 py-2.5">
                      {isExpired(u) ? (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid={`user-status-${u.email}`}>{t("u.expired")}</span>
                      ) : (
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`user-status-${u.email}`}>
                          {u.active ? t("u.active") : t("u.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs" data-testid={`user-expiry-${u.email}`}>
                      {u.expires_at ? fmtDate(u.expires_at) : t("u.no_limit")}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(u)} data-testid={`user-edit-${u.email}`} className="text-xs font-bold text-[#033A8C] hover:text-[#044CBC] mr-3 inline-flex items-center gap-1">
                        <Pencil className="w-3 h-3" /> {t("u.edit")}
                      </button>
                      {u.id !== user.id && (
                        <button
                          onClick={() => patch(u.id, { active: !u.active })}
                          data-testid={`user-toggle-${u.email}`}
                          className={`text-xs font-bold ${u.active ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}
                        >
                          {u.active ? t("u.deactivate") : t("u.activate")}
                        </button>
                      )}
                      {user.is_super && u.id !== user.id && (
                        <button onClick={() => remove(u)} data-testid={`user-delete-${u.email}`} className="ml-3 text-xs font-bold text-red-700 hover:text-red-900 inline-flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> {t("u.delete")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
